import React, { useState } from 'react';
import styles from './WithdrawTable.module.scss';
import { IWithdrawTableProps, TWithdrawMethod } from '../../../../types/IWithdrawRequest';
import { Checkbox } from '../../../Shared/Checkbox/Checkbox';
import { ReactComponent as ArrowRight } from '../../../../assets/arrow-right.svg';
import { ReactComponent as SuccessSvg } from '../../../../assets/apply.svg';
import { ReactComponent as DeclineSvg } from '../../../../assets/decline.svg';
import { Badge } from '../../../Shared/Badge/Badge';
import { Verified } from '../../../Shared/Verified/Verified';
import { GreenButton } from '../../../Shared/GreenButton/GreenButton';
import { EWithdrawStatus } from '../../../../types/EWithdrawStatus';
import { useNavigate } from 'react-router-dom';
import { PopupConfirmDialog } from '../../../Shared/PopupConfirmDialog/PopupConfirmDialog';
import { useSetPaymentAccountVerified } from '../../../../hooks/useSetPaymentAccountVerified';
import { usePaymentRequestStatus } from '../../../../hooks/usePaymentRequestStatus';

const tableHeader: string[] = ['IBAN / CARD', 'NAME', 'DATE AND TIME', 'STATUS', 'AMOUNT, EUR'];

// Card withdrawals are executed automatically by the backend after approval, so the admin's
// "confirm" means Approved for them (the payout job takes it from there) and Succeeded only for
// manual IBAN transfers.
const isCardRequest = (request: { method: TWithdrawMethod }) => request.method === 'UnlimitCard';

export const WithdrawTable = ({ data, selectRequest, selectAll, refetch }: IWithdrawTableProps): React.JSX.Element => {
    const navigate = useNavigate();
    const { mutate: setVerify } = useSetPaymentAccountVerified();
    const { mutate: setPaymentRequestStatus } = usePaymentRequestStatus();
    const [verifyWithdrawRequestId, setVerifyWithdrawRequestId] = useState('');
    const [confirmRequestId, setConfirmRequestId] = useState('');
    const [declineRequestId, setDeclineRequestId] = useState('');

    const onConfirmVerify = (withdrawRequestId: string) => {
        setVerifyWithdrawRequestId(withdrawRequestId);
    };

    const onConfirmRequest = (withdrawRequestId: string) => {
        setConfirmRequestId(withdrawRequestId);
    };

    const onDeclineRequest = (withdrawRequestId: string) => {
        setDeclineRequestId(withdrawRequestId);
    };

    const onConfirm = () => {
        if (confirmRequestId) {
            const confirmTarget = data?.find((el) => el.id === confirmRequestId);
            setPaymentRequestStatus(
                {
                    requestId: confirmRequestId,
                    status: confirmTarget && isCardRequest(confirmTarget) ? EWithdrawStatus.APPROVED : EWithdrawStatus.SUCCESS,
                },
                {
                    onSuccess() {
                        onCloseModal();
                        refetch();
                    },
                }
            );
        }
    };

    const onDecline = () => {
        if (declineRequestId) {
            setPaymentRequestStatus(
                {
                    requestId: declineRequestId,
                    status: EWithdrawStatus.ERROR,
                },
                {
                    onSuccess() {
                        onCloseModal();
                        refetch();
                    },
                }
            );
        }
    };

    const onVerify = () => {
        const currentRequest = data?.find((el) => el.id === verifyWithdrawRequestId);
        if (currentRequest && currentRequest.iban) {
            setVerify(
                {
                    iban: currentRequest.iban,
                    isVerified: true,
                    userId: currentRequest.userId,
                },
                {
                    onSuccess() {
                        onCloseModal();
                        refetch();
                    },
                }
            );
        }
    };

    const onCloseModal = () => {
        setVerifyWithdrawRequestId('');
        setConfirmRequestId('');
        setDeclineRequestId('');
    };

    if (!data) return <></>;

    return (
        <div className={styles.container}>
            <PopupConfirmDialog
                title="User verification"
                description="Are you sure you want to verify this user?"
                show={!!verifyWithdrawRequestId}
                onClose={onCloseModal}
                onConfirm={onVerify}
            />

            <PopupConfirmDialog
                title="Confirm transaction"
                description={`Are you sure you want to CONFIRM withdraw transaction with ID: ${confirmRequestId}`}
                show={!!confirmRequestId}
                onClose={onCloseModal}
                onConfirm={onConfirm}
            />

            <PopupConfirmDialog
                title="Decline transaction"
                description={`Are you sure you want to DECLINE withdraw transaction with ID: ${declineRequestId}`}
                show={!!declineRequestId}
                onClose={onCloseModal}
                onConfirm={onDecline}
            />

            <table className={styles.table}>
                <thead>
                    <tr>
                        <td>
                            <Checkbox
                                checked={data.filter((data) => data.isSelected).length === data.length}
                                onChange={selectAll}
                            />
                        </td>
                        {tableHeader.map((headerElement) => (
                            <td key={headerElement}>{headerElement}</td>
                        ))}
                        <td></td>
                        <td></td>
                    </tr>
                </thead>
                <tbody>
                    {data.map((withdrawRequest, idx) => (
                        <tr key={withdrawRequest.id} className={idx % 2 ? '' : styles.grayColl}>
                            <td>
                                <Checkbox
                                    onChange={() => {
                                        selectRequest(withdrawRequest.id);
                                    }}
                                    checked={withdrawRequest.isSelected}
                                />
                            </td>
                            <td>
                                {isCardRequest(withdrawRequest)
                                    ? `${withdrawRequest.cardBrand ?? 'Card'} ${withdrawRequest.cardMaskedPan} (${withdrawRequest.cardExpiration})`
                                    : withdrawRequest.iban}
                            </td>
                            <td>
                                <div className={styles.row}>
                                    <div className={styles.name}>
                                        {withdrawRequest.lastName} {withdrawRequest.firstName}{' '}
                                        {withdrawRequest.isVerified}
                                    </div>
                                    <Verified isVerified={withdrawRequest.isVerified} />
                                </div>
                            </td>
                            <td>{withdrawRequest.createdAt}</td>
                            <td>
                                <div className={styles.row}>
                                    <Badge status={withdrawRequest.status} />
                                    {withdrawRequest.status === EWithdrawStatus.IN_PROGRESS && (
                                        <>
                                            <div
                                                className={styles.button}
                                                onClick={() => {
                                                    onConfirmRequest(withdrawRequest.id);
                                                }}
                                            >
                                                <SuccessSvg />
                                            </div>
                                            <div
                                                className={styles.button}
                                                onClick={() => {
                                                    onDeclineRequest(withdrawRequest.id);
                                                }}
                                            >
                                                <DeclineSvg />
                                            </div>
                                        </>
                                    )}
                                    {withdrawRequest.status === EWithdrawStatus.APPROVED && isCardRequest(withdrawRequest) && (
                                        <div
                                            className={styles.button}
                                            onClick={() => {
                                                onDeclineRequest(withdrawRequest.id);
                                            }}
                                        >
                                            <DeclineSvg />
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td>{withdrawRequest.amount}</td>
                            <td>
                                <div className={styles.row}>
                                    {!withdrawRequest.isVerified && !isCardRequest(withdrawRequest) && (
                                        <GreenButton
                                            noMargin
                                            mini
                                            onClick={() => {
                                                onConfirmVerify(withdrawRequest.id);
                                            }}
                                        >
                                            Verify
                                        </GreenButton>
                                    )}
                                </div>
                            </td>
                            <td>
                                <div
                                    className={styles.button}
                                    onClick={() => {
                                        navigate(`/withdraw-request/${withdrawRequest.id}`);
                                    }}
                                >
                                    <ArrowRight />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
